import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  HardDrives, Cloud, CheckCircle, XCircle, Warning, ArrowRight, ArrowClockwise,
  Info, PauseCircle, Play, ListChecks, Copy, Trash,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import LanProbesCard from './settings/LanProbesCard';
import EmergencyRestoreCard from './settings/EmergencyRestoreCard';
import { VerifyIntegrityCard } from './settings/VerifyIntegrityCard';
import { NasWizardCard } from './settings/NasWizardCard';
import { AdoptNasFilesCard } from './settings/AdoptNasFilesCard';
import { formatDateTime } from '@/utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APPS = [
  { key: 'shizai',      label: '資材アプリ',       desc: '発注書・受入写真・部品受注入力の添付' },
  { key: 'it',          label: 'IT アプリ',        desc: '（現時点で添付ファイルはありません）' },
  { key: 'mfg',         label: '製造アプリ',       desc: 'BOM・作業指示・在庫・設備の添付' },
  { key: 'knowledge',   label: 'ナレッジ',         desc: 'ナレッジページ内の画像・PDF' },
  { key: 'engineering', label: '技術アプリ',       desc: 'エンジニア日報 / 技術資産 / 技術フォームの添付（画像・PDF・Office・テキスト、25MBまで）' },
  { key: 'bihin',       label: '備品管理',         desc: '備品・貸出・消耗品・フロアマップの添付ファイル' },
  { key: 'dms',         label: '文書デスク',       desc: 'DMS 文書の版・スキャン取込・OCR成果物' },
  { key: 'ai',          label: 'AI 関連',          desc: 'AI インポート / RAG ソース / クローラー成果物などの将来用（実装時に自動で反映）' },
];

const BACKENDS = {
  emergent: { label: 'Emergent クラウド', Icon: Cloud,      klass: 'from-sky-400 to-blue-500' },
  nas:      { label: 'Asustor NAS',       Icon: HardDrives, klass: 'from-amber-400 to-orange-500' },
};

const fmtDate = (iso) => (iso ? formatDateTime(iso) : '—');

const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); toast.success('コピーしました'); }
  catch { toast.error('コピーに失敗しました'); }
};

const SetupStep = ({ num, title, children }) => (
  <div className="rounded-md border border-zinc-200 overflow-hidden">
    <div className="px-3 py-2 border-b border-zinc-200 flex items-center gap-2 font-semibold text-zinc-800 text-[12px]">
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-teal-700 text-white text-[11px] font-bold shrink-0">{num}</span>
      {title}
    </div>
    <div className="px-3 py-3">{children}</div>
  </div>
);

const NasCopyBlock = ({ code }) => (
  <div className="flex gap-2 items-start min-w-0">
    <pre className="flex-1 min-w-0 font-mono text-[11px] leading-relaxed px-3 py-2 rounded-md border border-zinc-800 bg-zinc-900 text-zinc-100 overflow-x-auto whitespace-pre-wrap break-all">{code}</pre>
    <Button onClick={() => copyText(code)} variant="outline" size="sm" className="shrink-0 h-auto py-2" title="コピー">
      <Copy size={14} />
    </Button>
  </div>
);

export default function StorageSettings() {
  const [config, setConfig] = useState(null);
  const [health, setHealth] = useState(null);
  const [saving, setSaving] = useState(null); // app key currently saving
  const [migrations, setMigrations] = useState([]);
  // Unified dialog. Two entry points:
  //   1. Toggle click     → { mode: 'toggle',  app, source, target, count }
  //   2. Copy button click → { mode: 'copy',    app, source, target, count }
  // Both offer the same two checkboxes: also copy existing files, and (if
  // copy is checked) delete source after verify (true move).
  const [dialog, setDialog] = useState(null);
  const [copyExisting, setCopyExisting] = useState(false);
  const [deleteSource, setDeleteSource] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, h, m] = await Promise.all([
        axios.get(`${API}/settings/storage`),
        axios.get(`${API}/settings/storage/health`),
        axios.get(`${API}/settings/storage/migrations`),
      ]);
      setConfig(c.data);
      setHealth(h.data);
      setMigrations(m.data?.migrations || []);
    } catch (e) {
      console.error('load storage config', e);
      toast.error('ストレージ設定の取得に失敗しました');
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Poll migrations while any is running
  useEffect(() => {
    const running = migrations.some((m) => m.status === 'running' || m.status === 'queued');
    if (!running) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [migrations, load]);

  // Open the unified dialog for a per-app toggle. The dialog decides whether
  // it just flips routing, kicks off a copy, or does a true "move".
  const setBackend = (app, backend) => {
    if (config?.apps?.[app] === backend) return;
    if (backend === 'nas' && !config?.nas_configured) {
      toast.error('NAS が設定されていません。バックエンド .env の NAS_STORAGE_URL / NAS_STORAGE_TOKEN を設定してください。');
      return;
    }
    const current = config?.apps?.[app] || 'emergent';
    const count = config?.file_counts?.[app] ?? 0;
    setCopyExisting(false);
    setDeleteSource(false);
    setDialog({ mode: 'toggle', app, source: current, target: backend, count });
  };

  const setAllBackends = async (backend) => {
    if (backend === 'nas' && !config?.nas_configured) {
      toast.error('NAS が設定されていません。');
      return;
    }
    if (!window.confirm(`すべてのアプリの新規アップロード先を「${BACKENDS[backend].label}」に切り替えますか？\n\n既存のファイルはそのままの場所に残り、引き続き読み込み可能です。`)) return;
    setSaving('__all__');
    try {
      await axios.put(`${API}/settings/storage/all`, { backend });
      toast.success(`全アプリ → ${BACKENDS[backend].label}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'バックエンドの一括変更に失敗しました');
    } finally { setSaving(null); }
  };

  // Copy button (or true-move) — same dialog, but the routing is NOT flipped.
  const startMigrate = (app, source, target) => {
    const count = config?.file_counts?.[app] ?? 0;
    setCopyExisting(true); // implicit — user pressed the copy button
    setDeleteSource(false);
    setDialog({ mode: 'copy', app, source, target, count });
  };

  const closeDialog = () => {
    setDialog(null);
    setCopyExisting(false);
    setDeleteSource(false);
  };

  const confirmDialog = async () => {
    if (!dialog) return;
    const { mode, app, source, target } = dialog;
    // Guard: if the user unchecked copy but this was a copy dialog, we treat
    // it as a cancel (no-op). If the user checked delete without copy, force
    // copy on (safer default).
    const willCopy = mode === 'copy' ? true : copyExisting;
    const willDelete = willCopy && deleteSource;

    setSaving(app);
    let flipped = false;
    try {
      // Step 1 — flip routing (only for toggle mode)
      if (mode === 'toggle') {
        await axios.put(`${API}/settings/storage`, { app, backend: target });
        flipped = true;
        toast.success(`${APPS.find((a) => a.key === app)?.label} → ${BACKENDS[target].label}`);
      }
      // Step 2 — optional copy / true-move
      if (willCopy) {
        try {
          await axios.post(`${API}/settings/storage/migrations`, {
            app, source, target, delete_source: willDelete,
          });
          toast.success(willDelete ? '移動を開始しました（コピー後、元を削除）' : 'コピーを開始しました');
        } catch (copyErr) {
          // Half-commit protection — the toggle already flipped but the copy
          // failed to start. Roll the toggle back so admin isn't left with
          // "new uploads go to X, but existing files never moved" surprise.
          if (flipped) {
            try {
              await axios.put(`${API}/settings/storage`, { app, backend: source });
              toast.error('コピー開始に失敗したため、保存先を元に戻しました');
            } catch {
              toast.error('コピー失敗 + 保存先ロールバックも失敗。手動で確認してください。');
            }
          } else {
            toast.error(copyErr.response?.data?.detail || 'コピーの開始に失敗しました');
          }
          throw copyErr; // fall through to the outer catch (no closeDialog)
        }
      }
      closeDialog();
      load();
    } catch (e) {
      // Only show a generic toast if the inner block didn't already show one.
      if (!flipped) {
        toast.error(e.response?.data?.detail || '処理に失敗しました');
      }
      load();
    } finally { setSaving(null); }
  };

  const cancelMigrate = async (id) => {
    if (!window.confirm('この移行をキャンセルしますか？（コピー済みのファイルは残ります）')) return;
    try {
      await axios.post(`${API}/settings/storage/migrations/${id}/cancel`);
      toast.success('キャンセル要求を送信しました');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'キャンセルに失敗しました');
    }
  };

  const runningApps = new Set(migrations.filter((m) => m.status === 'running' || m.status === 'queued').map((m) => m.app));

  return (
    <div className="space-y-6 max-w-5xl" data-testid="storage-settings-page">
      <header>
        <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-zinc-400">設定 · システム</p>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 mt-1 flex items-center gap-2">
          <HardDrives size={26} weight="duotone" className="text-teal-700" />
          ファイルストレージ
        </h1>
        <p className="text-xs sm:text-sm text-zinc-500 mt-1 leading-relaxed">
          アップロードされたファイル（図面・写真・添付）の保存先を アプリごとに 選択します。
          切替時に <b>既存ファイルもまとめて移行</b> するか、<b>新規アップロードのみ切り替え</b> るかを選べます。
        </p>
      </header>

      {/* Health card */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="storage-health-card">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-50 text-amber-700 shrink-0">
            <HardDrives size={20} weight="duotone" />
          </span>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-zinc-900">NAS 接続状況</h2>
            <p className="text-[12px] text-zinc-500">Asustor NAS 上で動く Sensbey File Agent との接続確認。</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="storage-health-refresh" className="shrink-0">
            <ArrowClockwise size={14} className="mr-1" />
            再確認
          </Button>
        </div>
        <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-3 gap-4 text-[13px]">
          <div>
            <div className="text-[11px] text-zinc-500 font-semibold uppercase tracking-wide">状態</div>
            <div className="mt-1 flex items-center gap-1.5 font-semibold" data-testid="storage-health-status">
              {health?.ok ? (
                <><CheckCircle size={16} weight="duotone" className="text-emerald-600" /><span className="text-emerald-700">接続 OK</span></>
              ) : health ? (
                <><XCircle size={16} weight="duotone" className="text-rose-600" /><span className="text-rose-700">接続不可</span></>
              ) : (<span className="text-zinc-400">確認中…</span>)}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-zinc-500 font-semibold uppercase tracking-wide">エンドポイント</div>
            <div className="mt-1 font-mono text-[11px] text-zinc-700 truncate" title={config?.nas_url || ''} data-testid="storage-health-url">
              {config?.nas_url || <span className="text-zinc-400">未設定</span>}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-zinc-500 font-semibold uppercase tracking-wide">応答時間</div>
            <div className="mt-1 font-semibold text-zinc-900">
              {health?.latency_ms !== null && health?.latency_ms !== undefined ? `${health.latency_ms} ms` : '—'}
            </div>
          </div>
          {health && !health.ok && (
            <div className="sm:col-span-3 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-800 flex items-start gap-2">
              <Warning size={16} weight="duotone" className="shrink-0 mt-0.5" />
              <span>{health.message}</span>
            </div>
          )}
        </div>
      </div>

      {/* First-time onboarding wizard for admins — only appears when NAS is
          configured AND at least one app is still on Emergent AND user hasn't
          dismissed it. Otherwise silent. */}
      <NasWizardCard config={config} onFinished={load} />

      {/* LAN 直結ダウンロード設定 */}
      <LanProbesCard />

      {/* 緊急復旧 — NAS がオフラインでもバックアップから復元 */}
      <EmergencyRestoreCard />

      {/* 整合性チェック — DB とストレージのファイル参照の突合 */}
      <AdoptNasFilesCard />

      <VerifyIntegrityCard />

      {/* Per-app config */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="storage-apps-card">
        <div className="px-5 py-4 border-b border-zinc-200 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-zinc-900">アプリごとの保存先</h2>
            <p className="text-[12px] text-zinc-500 mt-0.5">切替はいつでも可能。ダイアログで <b>「既存もコピー」</b> や <b>「真の移動（元を削除）」</b> を選べます。</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[11px] text-zinc-500 hidden sm:inline">全アプリ一括:</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAllBackends('emergent')}
              disabled={saving === '__all__'}
              data-testid="storage-set-all-emergent"
              className="h-8 text-[12px]"
            >
              <Cloud size={12} weight="duotone" className="mr-1" />
              クラウドへ
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAllBackends('nas')}
              disabled={saving === '__all__' || !config?.nas_configured}
              data-testid="storage-set-all-nas"
              className="h-8 text-[12px]"
            >
              <HardDrives size={12} weight="duotone" className="mr-1" />
              NAS へ
            </Button>
          </div>
        </div>
        {config?.orphan_collections?.length > 0 && (
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-start gap-2 text-[12px] text-amber-900" data-testid="storage-orphan-warning">
            <Warning size={16} weight="duotone" className="shrink-0 mt-0.5" />
            <div>
              <b>未登録のファイルコレクションが検出されました:</b>{' '}
              {config.orphan_collections.map((o) => `${o.collection}(${o.count})`).join(', ')}
              <div className="text-[11px] mt-0.5">これらは新規アップロード先に従いますが、<b>移行エンジンには含まれません</b>。開発者に FILE_BUCKETS への追加を依頼してください。</div>
            </div>
          </div>
        )}
        <div className="divide-y divide-zinc-200" data-testid="storage-apps-list">
          {APPS.map((app) => {
            const current = config?.apps?.[app.key] || 'emergent';
            const count = config?.file_counts?.[app.key] ?? 0;
            const running = runningApps.has(app.key);
            const other = current === 'nas' ? 'emergent' : 'nas';
            return (
              <div key={app.key} className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4" data-testid={`storage-app-${app.key}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-zinc-900 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    {app.label}
                    {count > 0 && (
                      <span className="text-[11px] font-normal text-zinc-500">
                        · 既存 <b className="font-semibold text-zinc-700">{count.toLocaleString()}</b> ファイル
                      </span>
                    )}
                  </div>
                  <div className="text-[12px] text-zinc-500 mt-0.5">{app.desc}</div>
                </div>

                {/* Segmented control — the active option IS the current backend
                    (removes the redundant "選択中" grey label and the standalone
                    "current backend" badge that were confusing users) */}
                <div
                  role="radiogroup"
                  aria-label="保存先を選択"
                  className="inline-flex rounded-lg bg-zinc-100 p-1 border border-zinc-200 shrink-0"
                >
                  {Object.entries(BACKENDS).map(([bk, meta]) => {
                    const Icon = meta.Icon;
                    const active = current === bk;
                    return (
                      <button
                        key={bk}
                        onClick={() => !active && setBackend(app.key, bk)}
                        disabled={saving === app.key || active}
                        role="radio"
                        aria-checked={active}
                        data-testid={`storage-app-${app.key}-set-${bk}`}
                        title={active ? `現在この保存先を使用中` : `${meta.label} に切り替える`}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold whitespace-nowrap transition-all ${
                          active
                            ? `bg-gradient-to-br ${meta.klass} text-white shadow-sm cursor-default`
                            : 'text-zinc-600 hover:bg-white hover:text-zinc-900 hover:shadow-sm'
                        }`}
                      >
                        <Icon size={13} weight="duotone" />
                        {meta.label}
                      </button>
                    );
                  })}
                </div>

                {/* Migration button — only shown when there are existing files
                    on the current backend that would need copying over. The
                    IT app never has attachments, so hide the button entirely
                    there. */}
                {app.key !== 'it' && count > 0 && (
                  <button
                    onClick={() => startMigrate(app.key, current, other)}
                    disabled={running}
                    data-testid={`storage-app-${app.key}-migrate`}
                    title={running
                      ? '移行実行中'
                      : `既存の ${count.toLocaleString()} 件を ${BACKENDS[other].label} にコピー（元は残ります）`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-teal-700 text-teal-700 bg-white hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed text-[12px] font-semibold whitespace-nowrap transition-colors shrink-0"
                  >
                    {running ? (
                      <>
                        <ArrowClockwise size={13} weight="bold" className="animate-spin" />
                        移行中…
                      </>
                    ) : (
                      <>
                        <ArrowRight size={13} weight="bold" />
                        {BACKENDS[other].label}へコピー
                      </>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Migrations table */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="storage-migrations-card">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-50 text-indigo-700 shrink-0">
            <ListChecks size={20} weight="duotone" />
          </span>
          <div className="flex-1">
            <h2 className="font-bold text-zinc-900">移行履歴</h2>
            <p className="text-[12px] text-zinc-500">直近 50 件。「移動」と付いた行はコピー検証後に移行元を削除します。</p>
          </div>
        </div>
        <div className="px-5 py-4">
          {migrations.length === 0 ? (
            <div className="text-[13px] text-zinc-400 flex items-center gap-2" data-testid="storage-migrations-empty">
              <Info size={14} weight="duotone" />
              まだ移行はありません。
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-left text-zinc-500 border-b border-zinc-200">
                    <th className="py-2 pr-3">アプリ</th>
                    <th className="py-2 pr-3">方向</th>
                    <th className="py-2 pr-3">状態</th>
                    <th className="py-2 pr-3">進捗</th>
                    <th className="py-2 pr-3">実行者</th>
                    <th className="py-2 pr-3">開始</th>
                    <th className="py-2 pl-3 text-right"> </th>
                  </tr>
                </thead>
                <tbody>
                  {migrations.map((m) => {
                    const done = (m.counts?.copied || 0) + (m.counts?.skipped || 0) + (m.counts?.failed || 0);
                    const total = m.counts?.total || 0;
                    const pct = total ? Math.floor((done / total) * 100) : 0;
                    const st = m.status;
                    const badge = {
                      running: 'bg-blue-50 text-blue-700 border-blue-200',
                      queued: 'bg-zinc-100 text-zinc-600 border-zinc-200',
                      completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                      completed_with_errors: 'bg-amber-50 text-amber-700 border-amber-200',
                      cancelled: 'bg-zinc-100 text-zinc-500 border-zinc-200',
                      failed: 'bg-rose-50 text-rose-700 border-rose-200',
                    }[st] || 'bg-zinc-100 text-zinc-600 border-zinc-200';
                    return (
                      <tr key={m.id} className="border-b border-zinc-200 last:border-b-0" data-testid={`storage-migration-row-${m.id}`}>
                        <td className="py-2.5 pr-3 font-semibold text-zinc-900">{APPS.find((a) => a.key === m.app)?.label || m.app}</td>
                        <td className="py-2.5 pr-3 text-zinc-600 whitespace-nowrap">
                          {BACKENDS[m.source]?.label} → {BACKENDS[m.target]?.label}
                          {m.delete_source && (
                            <span
                              className="ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-700 border border-rose-200"
                              title="移動: コピー検証後に移行元を削除"
                              data-testid={`storage-migration-move-badge-${m.id}`}
                            >
                              <Trash size={10} weight="bold" />
                              移動
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 pr-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${badge}`}>
                            {{ running: '実行中', queued: '待機中', completed: '完了', completed_with_errors: '一部エラー', cancelled: 'キャンセル', failed: '失敗' }[st] || st}
                          </span>
                        </td>
                        <td className="py-2.5 pr-3 min-w-[140px]">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                              <div className="h-full bg-teal-700" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="tabular-nums text-zinc-600 shrink-0">{done}/{total}</span>
                          </div>
                          {(m.counts?.failed || 0) > 0 && (
                            <div className="text-[10px] text-rose-600 mt-0.5">失敗 {m.counts.failed}</div>
                          )}
                          {m.delete_source && (m.counts?.deleted_source || 0) > 0 && (
                            <div className="text-[10px] text-rose-500 mt-0.5" title="移行元から削除済みのファイル数">
                              元削除 {m.counts.deleted_source}
                            </div>
                          )}
                        </td>
                        <td className="py-2.5 pr-3 text-zinc-500 truncate max-w-[160px]" title={m.started_by_email}>{m.started_by_email || '—'}</td>
                        <td className="py-2.5 pr-3 text-zinc-500 whitespace-nowrap">{fmtDate(m.started_at)}</td>
                        <td className="py-2.5 pl-3 text-right">
                          {(st === 'running' || st === 'queued') && (
                            <Button size="sm" variant="outline" onClick={() => cancelMigrate(m.id)} data-testid={`storage-migration-cancel-${m.id}`} className="h-7 px-2 text-[11px]">
                              <PauseCircle size={12} className="mr-1" />
                              停止
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Unified confirmation dialog — used by BOTH:
          - Toggle click (mode:'toggle') → flip routing, optionally copy, optionally move
          - Copy button   (mode:'copy')   → copy existing, optionally move (source delete)
      */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent data-testid="storage-migrate-dialog" className="max-w-lg">
          {dialog && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {dialog.mode === 'toggle' ? (
                    <>
                      <ArrowRight size={20} weight="duotone" className="text-teal-700" />
                      保存先を切り替え
                    </>
                  ) : (
                    <>
                      <Play size={20} weight="duotone" className="text-teal-700" />
                      既存ファイルの一括コピー
                    </>
                  )}
                </DialogTitle>
                <DialogDescription>
                  <b>{APPS.find((a) => a.key === dialog.app)?.label}</b>
                  {dialog.mode === 'toggle' ? (
                    <>
                      {' '}の新規アップロード先を
                      <b> {BACKENDS[dialog.source]?.label}</b> から
                      <b> {BACKENDS[dialog.target]?.label}</b> に切り替えます。
                    </>
                  ) : (
                    <>
                      {' '}の既存ファイルを
                      <b> {BACKENDS[dialog.source]?.label}</b> から
                      <b> {BACKENDS[dialog.target]?.label}</b> へコピーします。
                    </>
                  )}
                </DialogDescription>
              </DialogHeader>

              <div className="text-[12px] text-zinc-700 space-y-3 py-2">
                {/* Toggle explainer */}
                {dialog.mode === 'toggle' && (
                  <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 flex items-start gap-2">
                    <Info size={14} weight="duotone" className="text-blue-600 shrink-0 mt-0.5" />
                    <span>
                      <b>切替後の新規アップロード</b>だけが <b>{BACKENDS[dialog.target]?.label}</b> に送られます。
                      既存の <b>{dialog.count.toLocaleString()}</b> 件は既定では移動しません（下のチェックで一緒に移せます）。
                    </span>
                  </div>
                )}

                {/* Checkboxes — only meaningful if there ARE existing files */}
                {dialog.count > 0 && (
                  <div className="rounded-md border border-zinc-200 divide-y divide-zinc-200">
                    {/* ── Copy checkbox ── (hidden in copy mode — always on) */}
                    {dialog.mode === 'toggle' && (
                      <label
                        htmlFor="chk-copy-existing"
                        className="flex items-start gap-3 px-3 py-2.5 cursor-pointer hover:bg-zinc-50"
                      >
                        <Checkbox
                          id="chk-copy-existing"
                          checked={copyExisting}
                          onCheckedChange={(v) => {
                            const next = !!v;
                            setCopyExisting(next);
                            if (!next) setDeleteSource(false);
                          }}
                          data-testid="storage-dialog-copy-existing"
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-zinc-900 flex items-center gap-1.5">
                            <Copy size={13} weight="duotone" className="text-teal-700" />
                            既存の {dialog.count.toLocaleString()} 件も {BACKENDS[dialog.target]?.label} にコピーする
                          </div>
                          <div className="text-[11px] text-zinc-500 mt-0.5">
                            SHA-256 で検証しながらコピー。バックグラウンドで実行され、進捗は下の「移行履歴」で追えます。
                          </div>
                        </div>
                      </label>
                    )}

                    {/* ── Delete-source checkbox ── (true move) */}
                    <label
                      htmlFor="chk-delete-source"
                      className={`flex items-start gap-3 px-3 py-2.5 ${
                        (dialog.mode === 'copy' || copyExisting)
                          ? 'cursor-pointer hover:bg-rose-50'
                          : 'opacity-50 cursor-not-allowed bg-zinc-50'
                      }`}
                    >
                      <Checkbox
                        id="chk-delete-source"
                        checked={deleteSource}
                        disabled={!(dialog.mode === 'copy' || copyExisting)}
                        onCheckedChange={(v) => setDeleteSource(!!v)}
                        data-testid="storage-dialog-delete-source"
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-zinc-900 flex items-center gap-1.5">
                          <Trash size={13} weight="duotone" className="text-rose-600" />
                          コピー後、移行元({BACKENDS[dialog.source]?.label})のファイルを削除する
                          <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">
                            真の移動
                          </span>
                        </div>
                        <div className="text-[11px] text-zinc-500 mt-0.5">
                          コピー・検証・DB更新の <b>すべて成功した後</b> に移行元を削除します（ロールバック不可）。
                          {dialog.mode === 'toggle' && !copyExisting && (
                            <span className="text-zinc-400"> · 「既存もコピー」を有効にすると選択できます。</span>
                          )}
                        </div>
                      </div>
                    </label>
                  </div>
                )}

                {/* Danger banner when delete is enabled */}
                {deleteSource && (
                  <div className="rounded-md bg-rose-50 border border-rose-300 px-3 py-2 flex items-start gap-2">
                    <Warning size={14} weight="duotone" className="text-rose-600 shrink-0 mt-0.5" />
                    <span className="text-rose-900">
                      <b>元のファイルは削除されます。</b>
                      コピー成功 → 検証 OK → DB フラグ切替 の 3 段階を通過したファイルのみ削除するため
                      データ喪失の心配はありませんが、<b>ロールバックはできません</b>。
                    </span>
                  </div>
                )}

                {/* Safety banner when only copying */}
                {!deleteSource && (dialog.mode === 'copy' || copyExisting) && (
                  <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 flex items-start gap-2">
                    <CheckCircle size={14} weight="duotone" className="text-emerald-600 shrink-0 mt-0.5" />
                    <span><b>元ファイルは削除されません</b>。両方の場所にファイルが存在する状態になります（ロールバック可能）。</span>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={closeDialog}>キャンセル</Button>
                <Button
                  onClick={confirmDialog}
                  data-testid="storage-migrate-confirm"
                  disabled={saving === dialog.app}
                  className={deleteSource
                    ? 'bg-rose-600 hover:bg-rose-700 text-white'
                    : 'bg-teal-700 hover:bg-teal-800'}
                >
                  {saving === dialog.app ? (
                    <><ArrowClockwise size={14} className="mr-1 animate-spin" />処理中…</>
                  ) : deleteSource ? (
                    <><Trash size={14} className="mr-1" />移動を開始</>
                  ) : (dialog.mode === 'copy' || copyExisting) ? (
                    <><Play size={14} className="mr-1" />{dialog.mode === 'toggle' ? '切替＋コピー開始' : 'コピーを開始'}</>
                  ) : (
                    <><ArrowRight size={14} className="mr-1" />切り替える</>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Setup guide */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="storage-setup-guide">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-50 text-zinc-600 shrink-0">
            <ListChecks size={20} weight="duotone" />
          </span>
          <div>
            <h2 className="font-bold text-zinc-900">初回セットアップ手順（NAS 側）</h2>
            <p className="text-[12px] text-zinc-500">Asustor NAS で Docker + 外部公開の 5 ステップ。実測 10-15 分で完了します。</p>
          </div>
        </div>
        <div className="px-5 py-4 text-[13px] text-zinc-700 leading-relaxed space-y-4">
          <p className="text-[12px] text-zinc-600">本アプリはクラウドで動作しているため、NAS 側で <b>HTTP API を提供する小さなエージェント</b> を Docker で動かし、外部から到達できるようにします。以下は Asustor（ADM）を想定していますが、Synology / QNAP / 一般 Linux でも同じ手順で動きます。</p>

          <SetupStep num={1} title="事前準備 — Docker + NAS を外部公開する仕組み">
            <ul className="list-disc pl-5 text-[12px] space-y-1">
              <li><b>Docker</b> をインストール（Asustor App Central →「Docker」または「Portainer」ワンクリック）</li>
              <li><b>NAS 外部公開</b> — 以下いずれかが必要:
                <ul className="list-[circle] pl-5 mt-1 space-y-0.5">
                  <li><b>Tailscale</b>（無料 · 推奨。App Central からインストール）</li>
                  <li><b>ZeroTier</b> / <b>AstroWarp</b>（既に導入済みなら再利用可）</li>
                  <li><b>Cloudflare Tunnel</b>（VPN 不要 · <code className="bg-zinc-100 px-1 rounded">cloudflared</code> Docker）</li>
                  <li>Asustor DDNS（<code className="bg-zinc-100 px-1 rounded">*.atwp.net</code>）+ ポートフォワード（HTTPS 未対応 · 推奨せず）</li>
                </ul>
              </li>
            </ul>
          </SetupStep>

          <SetupStep num={2} title="ソースを NAS に配置（SSH）">
            <p className="text-[12px] mb-1">作業ディレクトリを作って移動:</p>
            <NasCopyBlock code={`mkdir -p ~/sensbey-nas-agent && cd ~/sensbey-nas-agent`} />
            <p className="text-[12px] mt-2 mb-1">Dockerfile を作成:</p>
            <NasCopyBlock code={`cat > Dockerfile << 'EOF'
FROM python:3.11-alpine
WORKDIR /app
COPY main.py /app/main.py
RUN pip install --no-cache-dir fastapi uvicorn
VOLUME ["/data"]
EXPOSE 8788
ENV SENSBEY_NAS_DATA_DIR=/data HOST=0.0.0.0 PORT=8788
CMD ["python", "/app/main.py"]
EOF`} />
            <p className="text-[12px] mt-2 mb-1">main.py を作成（ヒアドキュメント全体をそのまま貼り付け）:</p>
            <NasCopyBlock code={`cat > main.py << 'PYEOF'
"""Sensbey NAS File Agent."""
import hashlib, mimetypes, os, pathlib
from fastapi import FastAPI, HTTPException, Request
from fastapi.responses import Response, JSONResponse

TOKEN = os.environ.get("SENSBEY_NAS_TOKEN") or ""
DATA_DIR = pathlib.Path(os.environ.get("SENSBEY_NAS_DATA_DIR") or "/data").resolve()
if not TOKEN:
    raise SystemExit("SENSBEY_NAS_TOKEN env var is required")
DATA_DIR.mkdir(parents=True, exist_ok=True)

app = FastAPI(title="Sensbey NAS File Agent", version="1.0.0")

def _auth(request):
    hdr = request.headers.get("authorization") or ""
    if not hdr.lower().startswith("bearer "):
        raise HTTPException(status_code=401, detail="missing bearer token")
    if hdr[7:].strip() != TOKEN:
        raise HTTPException(status_code=401, detail="invalid token")

def _safe_target(key):
    if not key or key.startswith("/"):
        raise HTTPException(status_code=400, detail="invalid key")
    target = (DATA_DIR / key).resolve()
    if DATA_DIR not in target.parents and target != DATA_DIR:
        raise HTTPException(status_code=400, detail="key escapes data directory")
    return target

@app.get("/health")
async def health(request: Request):
    _auth(request)
    try:
        st = os.statvfs(DATA_DIR); free_bytes = st.f_bavail*st.f_frsize; total_bytes = st.f_blocks*st.f_frsize
    except Exception:
        free_bytes = total_bytes = None
    return JSONResponse({"ok": True, "message": "NAS agent running",
                         "data_dir": str(DATA_DIR), "free_bytes": free_bytes,
                         "total_bytes": total_bytes, "version": "1.0.0"})

@app.put("/objects/{key:path}")
async def put_object(key: str, request: Request):
    _auth(request); body = await request.body()
    if not body: raise HTTPException(status_code=400, detail="empty body")
    target = _safe_target(key); target.parent.mkdir(parents=True, exist_ok=True)
    tmp = target.with_suffix(target.suffix + ".part"); tmp.write_bytes(body); os.replace(tmp, target)
    etag = hashlib.sha256(body).hexdigest()
    ct = request.headers.get("content-type") or mimetypes.guess_type(str(target))[0] or "application/octet-stream"
    (target.with_suffix(target.suffix + ".ct")).write_text(ct)
    return {"ok": True, "path": key, "size": len(body), "etag": etag}

@app.get("/objects/{key:path}")
async def get_object(key: str, request: Request):
    _auth(request); target = _safe_target(key)
    if not target.exists() or target.is_dir(): raise HTTPException(status_code=404, detail="not found")
    data = target.read_bytes(); ct_file = target.with_suffix(target.suffix + ".ct")
    ct = ct_file.read_text().strip() if ct_file.exists() else (mimetypes.guess_type(str(target))[0] or "application/octet-stream")
    return Response(content=data, media_type=ct)

@app.head("/objects/{key:path}")
async def head_object(key: str, request: Request):
    _auth(request); target = _safe_target(key)
    if not target.exists() or target.is_dir(): return Response(status_code=404)
    size = target.stat().st_size; ct_file = target.with_suffix(target.suffix + ".ct")
    ct = ct_file.read_text().strip() if ct_file.exists() else (mimetypes.guess_type(str(target))[0] or "application/octet-stream")
    return Response(headers={"Content-Length": str(size), "Content-Type": ct})

@app.delete("/objects/{key:path}")
async def delete_object(key: str, request: Request):
    _auth(request); target = _safe_target(key)
    if not target.exists() or target.is_dir(): return JSONResponse({"ok": False, "deleted": False}, status_code=404)
    target.unlink(); ct_file = target.with_suffix(target.suffix + ".ct")
    if ct_file.exists(): ct_file.unlink()
    return {"ok": True, "deleted": True}

if __name__ == "__main__":
    import uvicorn
    uvicorn.run(app, host=os.environ.get("HOST", "0.0.0.0"), port=int(os.environ.get("PORT", "8788")))
PYEOF`} />
            <p className="text-[11px] text-zinc-500 mt-1.5">確認: <code className="bg-zinc-100 px-1 rounded">ls -la</code> で Dockerfile と main.py の 2 ファイルが見えれば OK。</p>
          </SetupStep>

          <SetupStep num={3} title="Docker イメージをビルド + 起動">
            <p className="text-[12px] mb-1">ビルド（約 60 秒）:</p>
            <NasCopyBlock code={`sudo docker build -t sensbey-nas-agent:local .`} />
            <p className="text-[12px] mt-2 mb-1">32 バイトのランダムトークンを生成 <b className="text-rose-600">→ 出力を必ずコピー</b>:</p>
            <NasCopyBlock code={`openssl rand -hex 32`} />
            <p className="text-[12px] mt-2 mb-1">保存先を作成:</p>
            <NasCopyBlock code={`sudo mkdir -p "/volume1/Web/Sensbey Web App/files"`} />
            <p className="text-[12px] mt-2 mb-1">コンテナ起動（<b className="text-rose-600">&lt;トークン&gt;</b> を差し替えて 1 行で実行 · シングルクォート必須）:</p>
            <NasCopyBlock code={`sudo docker run -d --name sensbey-nas-agent --restart unless-stopped -p 8788:8788 -e SENSBEY_NAS_TOKEN='<トークン>' -v '/volume1/Web/Sensbey Web App/files:/data' sensbey-nas-agent:local`} />
            <p className="text-[12px] mt-2 mb-1">動作確認（NAS 内部）:</p>
            <NasCopyBlock code={`curl -H "Authorization: Bearer <トークン>" http://localhost:8788/health`} />
            <p className="text-[11px] text-zinc-500 mt-1">出力に <code className="bg-zinc-100 px-1 rounded">{'"ok":true'}</code> と <code className="bg-zinc-100 px-1 rounded">free_bytes</code> が含まれれば成功。</p>
          </SetupStep>

          <SetupStep num={4} title="外部から到達できるか確認">
            <p className="text-[12px] mb-1">Step 1 で選んだ手段のアドレスを使って、<b>NAS 以外の PC から</b> テスト:</p>
            <ul className="list-disc pl-5 text-[11px] space-y-0.5 text-zinc-600">
              <li>Tailscale: <code className="bg-zinc-100 px-1 rounded">http://&lt;Tailscale IP&gt;:8788/health</code></li>
              <li>AstroWarp: <code className="bg-zinc-100 px-1 rounded">http://&lt;仮想ドメイン&gt;.atwp.net:8788/health</code></li>
              <li>Cloudflare Tunnel: <code className="bg-zinc-100 px-1 rounded">https://nas.your-domain.com/health</code></li>
            </ul>
            <p className="text-[12px] mt-2 mb-1">例:</p>
            <NasCopyBlock code={`curl -H "Authorization: Bearer <トークン>" http://sensbey-nas.atwp.net:8788/health`} />
            <p className="text-[12px] mt-2">認証テスト（トークンなし → 401 が期待値）:</p>
            <NasCopyBlock code={`curl -o /dev/null -w "%{http_code}\\n" http://sensbey-nas.atwp.net:8788/health`} />
          </SetupStep>

          <SetupStep num={5} title="Sensbey Web App に接続情報を設定">
            <p className="text-[12px] mb-1">バックエンドの <code className="bg-zinc-100 px-1 rounded">.env</code> に以下を追加してデプロイ:</p>
            <NasCopyBlock code={`# ① ファイル保管（オブジェクトストレージ）
NAS_STORAGE_URL=http://sensbey-nas.atwp.net:8788
NAS_STORAGE_TOKEN=<Step 3 のトークン>

# ② AI学習クローラー（サーバー側からのみ使用・トークンは①を再利用）
NAS_CRAWLER_URL=http://localhost:8788
SENSBEY_NAS_CRAWL_ROOT=/crawl
NAS_CRAWLER_FALLBACK_ENABLED=false
NAS_CRAWLER_FALLBACK_URL=https://nas.sensbey.link`} />
            <p className="text-[12px] mt-2">
              <b>クローラーの接続先は「バックエンドが動いているサーバーから見た住所」です。</b>
              バックエンドを NAS と同じホストで動かしている場合のみ <code className="bg-zinc-100 px-1 rounded">http://localhost:8788</code> が有効です。
              クラウド（Emergent 等）で動かしている場合は、そこから NAS エージェントに到達できる住所
              （社内DNS名・VPN内アドレス・トンネル経由URL）を <code className="bg-zinc-100 px-1 rounded">NAS_CRAWLER_URL</code> に設定してください。
              「AI学習ソース &gt; NAS ドキュメント」の接続バッジは<b>クローラー経路そのもの</b>の状態を表示します（ストレージの健全性とは別）。
            </p>
            <p className="text-[12px] mt-2">
              AI学習クローラーを使う場合は、NAS エージェントを <b>v1.3.0 以降</b> にして、知識フォルダーを
              <code className="bg-zinc-100 px-1 rounded">-v '/volume1/技術:/crawl:ro'</code> のように
              <b>読み取り専用</b>で追加マウントしてください（フォルダー一覧APIが有効になります）。
            </p>
          </SetupStep>

          <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-800 flex items-start gap-2">
            <Warning size={16} weight="duotone" className="shrink-0 mt-0.5" />
            <span>
              <b>NAS がダウンしたら</b> NAS 保存のファイルは一時的にアクセスできなくなります（新規アップロードもエラー）。
              その間はこのページで各アプリを <b>Emergent クラウドへ</b> 戻せば、新規アップロードだけクラウドに送られ、
              NAS 復旧後は NAS 側のファイルも再度読めるようになります。<br />
              <b className="mt-1 inline-block">また URL が <code className="bg-amber-100 px-1 rounded">http://</code> のみの場合はトークンが平文で流れます。</b>
              可能であれば HTTPS 化（Cloudflare Tunnel / Caddy リバースプロキシ）を追加してください。
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
